import { computed, type Ref } from "vue";
import { useQuery, useMutation, useQueryClient } from "@tanstack/vue-query";
import { fetchWrapper } from "@/helpers/fetch-wrapper";
import type { ApiError } from "@/helpers/api-error";
import type { DocId, ChangeSet, RestoreChangeSet } from "@/types";

const BASE = `${import.meta.env.VITE_API_URL}/audit`;

interface FirstLoadResponse {
    documentTypes: string[];
    documentIds: DocId[];
}

interface ChangeSetDiffResponse {
    left: { metadata: string; json: string };
    right: { metadata: string; json: string };
}

// READ — document types (one-time, on mount)
export function useAuditDocumentTypes() {
    const { data, isFetching: isLoadingDocumentTypes, error } = useQuery({
        queryKey: ["audit", "document-types"],
        queryFn: () => fetchWrapper.get<FirstLoadResponse>(`${BASE}/GetFirstLoad?documentType=`),
    });
    const documentTypes = computed(() => data.value?.documentTypes ?? []);
    return { documentTypes, isLoadingDocumentTypes, error: error as Ref<ApiError | null> };
}

// READ — document IDs for a selected type
export function useAuditDocumentIds(documentType: Ref<string>) {
    const { data, isFetching: isLoadingDocumentIds, error } = useQuery({
        queryKey: computed(() => ["audit", "document-ids", documentType.value]),
        queryFn: () => fetchWrapper.get<DocId[]>(`${BASE}/GetDocumentIds?documentType=${documentType.value}`),
        enabled: computed(() => !!documentType.value),
    });
    const documentIds = computed(() => data.value ?? []);
    return { documentIds, isLoadingDocumentIds, error: error as Ref<ApiError | null> };
}

// READ — change sets for a selected document
export function useAuditChangeSets(documentType: Ref<string>, documentId: Ref<string>) {
    const { data, isFetching: isLoadingChangeSets, error } = useQuery({
        queryKey: computed(() => ["audit", "changesets", documentType.value, documentId.value]),
        queryFn: () => fetchWrapper.get<ChangeSet[]>(`${BASE}/GetChangeSetList?documentType=${documentType.value}&documentId=${documentId.value}`),
        enabled: computed(() => !!documentType.value && !!documentId.value),
    });
    const changeSets = computed(() => data.value ?? []);
    return { changeSets, isLoadingChangeSets, error: error as Ref<ApiError | null> };
}

// READ — diff between two changesets
export function useAuditChangeSetDiff(
    documentType: Ref<string>,
    documentId: Ref<string>,
    leftDocId: Ref<string>,
    rightDocId: Ref<string>
) {
    const { data, isFetching: isLoadingChangeSetDiff, error } = useQuery({
        queryKey: computed(() => ["audit", "diff", documentType.value, documentId.value, leftDocId.value, rightDocId.value]),
        queryFn: () => fetchWrapper.get<ChangeSetDiffResponse>(
            `${BASE}/GetChangesetDiff?documentType=${documentType.value}&documentId=${documentId.value}&leftDocId=${leftDocId.value}&rightDocId=${rightDocId.value}`
        ),
        enabled: computed(() => !!documentType.value && !!documentId.value && !!leftDocId.value && !!rightDocId.value),
    });
    const leftJson = computed(() => data.value?.left.json ?? "");
    const leftMetadata = computed(() => data.value?.left.metadata ?? "");
    const rightJson = computed(() => data.value?.right.json ?? "");
    const rightMetadata = computed(() => data.value?.right.metadata ?? "");
    return { leftJson, leftMetadata, rightJson, rightMetadata, isLoadingChangeSetDiff, error: error as Ref<ApiError | null> };
}

// WRITE — restore a changeset
export function useRestoreChangeset() {
    const queryClient = useQueryClient();
    const { mutate, isPending: restorePending, error, reset } = useMutation<unknown, ApiError, RestoreChangeSet>({
        mutationFn: (model) => fetchWrapper.post(`${BASE}/RestoreChangeset`, model),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ["audit", "changesets"] }),
    });
    return { restoreChangeset: mutate, restorePending, restoreError: error as Ref<ApiError | null>, restoreReset: reset };
}
